// ─── MSP Target Size Utilities ────────────────────────────────────────────────
// Exported separately so they can be unit-tested without React.

/**
 * Canonical real-world dimensions of the MSP/IPSC-style silhouette target.
 *
 * The body "A-zone" (down-zero / highest-value zone) is 5.9" × 11" per the
 * IPSC metric paper target spec, confirmed by Modern Samurai Project standards.
 * The "card head" is the 4" × 6" head A-zone on the IPSC metric target.
 *
 * We draw and score hits against the A-zone directly — no outer full-silhouette.
 */
export const TARGET_REAL = {
    // Body A-zone (the main scoring zone — what is shown and clicked for body shots)
    bodyZoneWidth: 5.9,  // inches wide  (IPSC metric A-zone confirmed by user)
    bodyZoneHeight: 11,   // inches tall

    // Head box ("card head" — the head A-zone above the body)
    headWidth: 4,   // inches wide  (IPSC metric head A-zone)
    headHeight: 6,   // inches tall

    // Distance from A-zone center up to head-box center (inches).
    // A-zone half-height (5.5") + ~2.5" gap + head half-height (3") ≈ 11"
    headOffsetY: 11,
};

/**
 * Convert a real-world measurement (inches) to canvas pixels so that the object
 * subtends the same visual angle as it would at distance `targetDistYd` yards,
 * when viewed from `viewDistIn` inches away on a screen that is `screenWidthIn`
 * inches wide and `screenWidthPx` pixels wide.
 *
 * @param {number} realInches     Real-world size of the object in inches.
 * @param {number} screenWidthPx  Canvas / screen width in pixels.
 * @param {number} screenWidthIn  Physical screen width in inches.
 * @param {number} viewDistIn     Eye-to-screen distance in inches.
 * @param {number} targetDistYd   Range to the real target in yards.
 * @returns {number} Pixel size that produces the correct angular size.
 */
export function inchesToPixels(realInches, screenWidthPx, screenWidthIn, viewDistIn, targetDistYd) {
    const pixelsPerInch = screenWidthPx / screenWidthIn;
    const targetDistIn = targetDistYd * 36; // yards → inches
    // Small-angle approximation: apparent size = realSize * viewDist / targetDist
    const screenSizeIn = (realInches * viewDistIn) / targetDistIn;
    return screenSizeIn * pixelsPerInch;
}

/**
 * Compute all target geometry (in canvas pixels) for a drill at a given distance,
 * given the user's screen calibration and canvas dimensions.
 *
 * Returns the A-zone (body) and head-box dimensions and centers.
 *
 * @param {object} params
 * @param {number} params.canvasWidth     Canvas width in pixels.
 * @param {number} params.canvasHeight    Canvas height in pixels.
 * @param {number} params.screenWidthIn   Physical screen width in inches.
 * @param {number} params.viewDistIn      Eye-to-screen distance in inches.
 * @param {number} params.targetDistYd    Target distance in yards.
 * @returns {{azoneW, azoneH, headW, headH, headOffY, bx, by, hx, hy}}
 */
export function computeTargetGeometry({ canvasWidth, canvasHeight, screenWidthIn, viewDistIn, targetDistYd }) {
    function px(realIn) {
        return inchesToPixels(realIn, canvasWidth, screenWidthIn, viewDistIn, targetDistYd);
    }

    const azoneW = px(TARGET_REAL.bodyZoneWidth);
    const azoneH = px(TARGET_REAL.bodyZoneHeight);
    const headW = px(TARGET_REAL.headWidth);
    const headH = px(TARGET_REAL.headHeight);
    const headOffY = px(TARGET_REAL.headOffsetY);

    // A-zone center: horizontally centred, vertically at 62% of canvas height
    const bx = canvasWidth / 2;
    const by = canvasHeight * 0.62;

    // Head center: directly above A-zone center by headOffY pixels
    const hx = bx;
    const hy = by - headOffY;

    return { azoneW, azoneH, headW, headH, headOffY, bx, by, hx, hy };
}

/**
 * The angular half-angle (in degrees) that an object of `realInches` subtends
 * at `targetDistYd` yards. Useful for sanity-checking the calibration.
 */
export function angularSizeDeg(realInches, targetDistYd) {
    const targetDistIn = targetDistYd * 36;
    return (2 * Math.atan(realInches / 2 / targetDistIn) * 180) / Math.PI;
}
